import { memo, RefAttributes } from 'react';
import { Text as RACText, TextProps as RACTextProps } from 'react-aria-components';

import { ComponentContextProps, merge, renderWithContext, useComponent } from '@/components';

export type TextProps = RACTextProps &
  RefAttributes<HTMLElement> &
  Partial<ComponentContextProps> & { size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' };

const IText = ({ variant, size, className, children, ...rest }: TextProps) => {
  const context = useComponent({ variant });

  let fontSize;
  switch (size) {
    case 'xs':
      fontSize = 'text-xs';
      break;
    case 'sm':
      fontSize = 'text-sm';
      break;
    case 'md':
      fontSize = 'text-md';
      break;
    case 'lg':
      fontSize = 'text-lg';
      break;
    case 'xl':
      fontSize = 'text-xl';
      break;
    default:
      fontSize = 'text-sm';
  }

  /* prettier-ignore */
  const styles = [
    'font-inter',
    fontSize,

    /* Button */
    'group-[*]/button:font-medium',

    /* FilterGroup */
    'group-[*]/filter-group:text-md',
    'group-[*]/filter-group:font-medium',
    'group-data-[selected]/filter-group:font-semibold',


    /* MenuItem */
    'group-[*]/menu-item:font-medium',
    'group-data-[selected]/menu-item:font-semibold',

    /* Field Cell */
    'group-[*]/field-cell:text-gray-800',

    /* AgentRun Row */
    'group-[*]/agent-run-row:text-gray-600',
    'group-[*]/agent-run-row:font-regular',

    /* Profile */
    'group-[*]/profile:font-semibold',
    'group-[*]/profile:text-sm',
    'group-[*]/profile:text-gray-800',

    /* th */
    'group-[*]/th:font-medium',
    'group-[*]/th:text-sm',

    /* Tab */
    'group-[*]/tab:font-semibold',

    /* Placeholder */
    'group-[*]/placeholder:text-gray-400',
  ];

  /* prettier-ignore */
  const plainStyles = [
    'text-gray-600',

     /* Button */
    'group-[*]/button:text-gray-600',
    'group-data-[disabled]/button:text-gray-400',
    'group-data-[pressed]/button:text-gray-700',
    'group-hover/button:text-gray-700',

    /* MenuItem */
    'group-[*]/menu-item:text-gray-700',
    'group-data-[disabled]/menu-item:text-gray-400',
    'group-data-[selected]/menu-item:text-gray-700',

    /* FilterGroup */
    'group-data-[selected]/filter-group:text-primary-700',

    /* FilterCard */
    'group-[*]/filter-card:text-primary-700',
    'group-[*]/filter-card:font-medium',
    'group-[*]/filter-card:hover:text-primary-700',

    /* Tab */
    'group-[*]/tab:text-gray-800',

    /* Radio */
    'group-[*]/radio:text-gray-700',
    'group-[*]/radio:font-medium',
    'group-[*]/radio:text-sm',

    /* Badge */
    'group-[*]/badge:text-gray-600',
    'group-[*]/badge:text-xs',

    /* Tutorial Box */
    'group-[*]/tutorial-box:text-gray-800',

    /* Save */
    'group-[*]/save:text-gray-500',
  ];

  /* prettier-ignore */
  const primaryStyles = [
    'text-primary-500',

    /* Button */
    'group-[*]/button:text-white',
    'group-data-[disabled]/button:text-gray-400',
    'group-data-[pressed]/button:text-white',
    'group-hover/button:text-white',

    /* Tooltip */
    'group-[*]/tooltip:text-gray-300',
    'group-[*]/tooltip:font-medium',
    'group-[*]/button:group-[*]/tooltip:text-gray-200',
    'group-[*]/tooltip:text-xs',

    /* Badge */
    'group-[*]/badge:text-xs',
    'group-[*]/badge:!text-primary-800',
  ];

  /* prettier-ignore */
  const secondaryStyles = [
    'text-gray-600',

    /* Button */
    'group-[*]/button:text-gray-700',
    'group-data-[disabled]/button:text-gray-400',
    'group-data-[pressed]/button:text-gray-800',
    'group-hover/button:text-gray-800',

    /* Radio */
    'group-[*]/radio:text-gray-600',
    'group-[*]/radio:regular',
    'group-[*]/radio:text-xs',

    /* Badge */
    'group-[*]/badge:text-gray-700',
  ];

  /* prettier-ignore */
  const tertiaryStyles = [
    'text-gray-500',

    /* Button */
    'group-[*]/button:text-gray-700',
    'group-data-[disabled]/button:text-gray-400',
    'group-data-[pressed]/button:text-gray-800',
    'group-hover/button:text-gray-800',

    /* Card */
    'group-[*]/card:font-regular',
    'group-[*]/card:text-gray-500',
  ];

  /* prettier-ignore */
  const successStyles = [
    'text-success-900',

    /* Button */
    'group-data-[disabled]/button:text-success-500',
    'group-data-[pressed]/button:text-success-700',
    'group-hover/button:text-success-700',

    /* Badge */
    'group-[*]/badge:text-success-900',
    'group-[*]/badge:text-xs',

    /* Autosave */
    'group-[*]/autosave:text-success-700',
  ];

  /* prettier-ignore */
  const warningStyles = [
    'text-warning-700',

    /* Button */
    'group-data-[disabled]/button:text-warning-500',
    'group-data-[pressed]/button:text-warning-700',
    'group-hover/button:text-warning-700',

    /* Badge */
    'group-[*]/badge:text-warning-900',
    'group-[*]/badge:text-xs',
  ];

  /* prettier-ignore */
  const errorStyles = [
    'text-error-700',

    /* Button */
    'group-data-[disabled]/button:text-error-500',
    'group-data-[pressed]/button:text-error-700',
    'group-hover/button:text-error-700',

    /* Badge */
    'group-[*]/badge:text-error-900',
    'group-[*]/badge:text-xs',
  ];

  /* prettier-ignore */
  const exportStyles = [
    'text-export-700',

    /* Button */
    'group-data-[disabled]/button:text-export-500',
    'group-data-[pressed]/button:text-export-700',
    'group-hover/button:text-export-700',
  ];

  /* prettier-ignore */
  const dangerStyles = [
    'text-error-900',

    /* Button */
    'group-[*]/button:text-danger-600',
    'group-data-[disabled]/button:text-gray-500',
    'group-data-[pressed]/button:text-error-500',
    'group-hover/button:text-error-500',

    /* Tooltip */
    'group-[*]/tooltip:text-gray-300',

    /* Badge */
    'group-[*]/badge:text-error-500',
    'group-[*]/badge:text-xs',
  ];

  return (
    <RACText
      className={merge(
        styles,
        context.variant === 'default' && plainStyles,
        context.variant === 'plain' && plainStyles,
        context.variant === 'primary' && primaryStyles,
        context.variant === 'secondary' && secondaryStyles,
        context.variant === 'tertiary' && tertiaryStyles,
        context.variant === 'success' && successStyles,
        context.variant === 'warning' && warningStyles,
        context.variant === 'error' && errorStyles,
        context.variant === 'export' && exportStyles,
        context.variant === 'danger' && dangerStyles,
        className
      )}
      {...rest}
    >
      {renderWithContext(children, context)}
    </RACText>
  );
};

export const Text = memo(IText) as typeof IText;
